/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import React, { useEffect } from 'react';
import FocusLock from 'react-focus-lock';

import { type CommonProps, type HtmlAttributes } from '../_common/types';
import { useDocumentScrollToggle } from '../_common/utils';
import { Portal } from '../portal';

export enum ModalCloseReason {
  BACKDROP_CLICK = 'backdropClick',
  ESCAPE_KEY_DOWN = 'escapeKeyDown',
}

/**
 * @remarks
 * Where `Element` is the most general base class from which all objects in a Document inherit. It only has methods and properties common to all kinds of elements. More specific classes inherit from Element.
 *
 * @remarks
 * Where `HtmlAttributes<"div">` is a type that includes all standard HTML div element attributes plus any custom data attributes (like data-testid or data-value) that follow the data-* pattern.
 */
export interface ModalProps {
  /** Whether the Modal is open. */
  isOpen: boolean;
  /** Content to render inside the Modal. */
  children: React.ReactNode;
  /** Sets the size(max-width) of the Modal, use 'unset' for custom sizing. */
  size?: 'small' | 'medium' | 'large' | 'unset';
  /** The container where the modal will be rendered. */
  container?: Element;
  /** Callback function called when the modal is closed. */
  onClose?: (
    e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    reason?: ModalCloseReason,
  ) => void;
  /** Forwards the props to the modal container's wrapper - use with caution */
  rootProps?: HtmlAttributes<'div'>;
}

export const Modal = ({
  isOpen: open,
  size = 'medium',
  onClose,
  children,
  container,
  rootProps,
  style,
  className,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'div', ModalProps>) => {
  const toggleScrollbar = useDocumentScrollToggle();
  const backdropClick = React.useRef<boolean | null>(null);

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    // We don't want to close the dialog when clicking the dialog content.
    // Make sure the event starts and ends on the same DOM element.
    backdropClick.current = event.target === event.currentTarget;
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.(
          e as unknown as React.KeyboardEvent<HTMLElement>,
          ModalCloseReason.ESCAPE_KEY_DOWN,
        );
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (Boolean(backdropClick.current) === false) {
      return;
    }

    backdropClick.current = null;
    onClose?.(e, ModalCloseReason.BACKDROP_CLICK);
  };

  useEffect(() => {
    toggleScrollbar(open);

    return () => toggleScrollbar(false);
  }, [open, toggleScrollbar]);

  if (!open) {
    return null;
  }

  const classes = classNames('ndl-modal', className, {
    'ndl-large': size === 'large',
    'ndl-medium': size === 'medium',
    'ndl-small': size === 'small',
  });
  const rootClasses = classNames('ndl-modal-root', rootProps?.className, {
    'ndl-modal-container': container,
  });

  return (
    <>
      <Portal container={container}>
        {/** This is to separate the colored backdrop with the blur from the modal. */}
        <div className="ndl-modal-backdrop" role="presentation" />
      </Portal>
      <Portal container={container}>
        <FocusLock hasPositiveIndices={true} returnFocus={true}>
          <div
            {...rootProps}
            className={rootClasses}
            role="presentation"
            onClick={handleBackdropClick}
            onMouseDown={handleMouseDown}
          >
            <div
              ref={ref}
              role="dialog"
              aria-modal="true"
              className={classes}
              style={style}
              {...restProps}
              {...htmlAttributes}
            >
              {children}
            </div>
          </div>
        </FocusLock>
      </Portal>
    </>
  );
};
